import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ToggleButton, ToggleButtonGroup, Button, CircularProgress, Slider, Typography } from '@mui/material'

// The fidelity check: the original beside the edit, zoomed together so one
// stone can be compared in both. Wipe stacks the pair and clips the original,
// which makes a reshaped prong or bail jump out.
const MIN_SCALE = 1
const MAX_SCALE = 8
const IDENTITY = { scale: 1, x: 0, y: 0 }

const BACKDROPS = {
    checker: {
        backgroundColor: '#fff',
        backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
    },
    white: { backgroundColor: '#fff' },
    dark: { backgroundColor: '#222' },
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

// Zoom about the pointer: keep the image point under the cursor fixed.
function zoomAbout(view, factor, px, py) {
    const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE)
    const ratio = scale / view.scale
    if (scale === MIN_SCALE) return IDENTITY
    return { scale, x: px - (px - view.x) * ratio, y: py - (py - view.y) * ratio }
}

// React registers wheel listeners as passive, so a JSX onWheel can't stop the
// page from scrolling while the reviewer zooms. This frame listens natively.
// Until the shot is active the wheel stays the page's, so scrolling past a
// photo scrolls the page instead of zooming it.
function ZoomFrame({ onZoom, enabled, children, ...props }) {
    const ref = useRef(null)
    useEffect(() => {
        const node = ref.current
        const listener = e => {
            if (!enabled) return
            e.preventDefault()
            onZoom(e, node)
        }
        node.addEventListener('wheel', listener, { passive: false })
        return () => node.removeEventListener('wheel', listener)
    }, [onZoom, enabled])
    return <Box ref={ref} {...props}>{children}</Box>
}

// `rawSrc`/`editedSrc` are the previews the page loads first; the full-size
// pair (`rawFullSrc`/`editedFullSrc`) replaces them the first time the reviewer
// zooms in, and stays, since it is loaded by then.
export default function ShotCompare({ rawSrc, editedSrc, rawFullSrc, editedFullSrc, label, busy, active }) {
    const { t } = useTranslation()
    const [mode, setMode] = useState('side')
    const [backdrop, setBackdrop] = useState('checker')
    const [wipe, setWipe] = useState(50)
    const [view, setView] = useState(IDENTITY)
    const pointers = useRef(new Map())
    const pinch = useRef(null)

    const [full, setFull] = useState(false)
    useEffect(() => { if (view.scale > 1) setFull(true) }, [view.scale])

    const transform = `translate(${view.x}px, ${view.y}px) scale(${view.scale})`

    const handleWheel = useCallback((e, node) => {
        const rect = node.getBoundingClientRect()
        const factor = Math.exp(-e.deltaY * 0.002)
        setView(prev => zoomAbout(prev, factor, e.clientX - rect.left, e.clientY - rect.top))
    }, [])

    function handlePointerDown(e) {
        if (!active) return
        e.currentTarget.setPointerCapture?.(e.pointerId)
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        if (pointers.current.size === 2) {
            const [a, b] = [...pointers.current.values()]
            pinch.current = Math.hypot(a.x - b.x, a.y - b.y)
        }
    }

    function handlePointerMove(e) {
        const last = pointers.current.get(e.pointerId)
        if (!last) return
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        if (pointers.current.size === 2 && pinch.current) {
            const [a, b] = [...pointers.current.values()]
            const distance = Math.hypot(a.x - b.x, a.y - b.y)
            const rect = e.currentTarget.getBoundingClientRect()
            const factor = distance / pinch.current
            pinch.current = distance
            setView(prev => zoomAbout(prev, factor, (a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top))
        } else if (pointers.current.size === 1) {
            setView(prev => (prev.scale === 1 ? prev : { ...prev, x: prev.x + e.clientX - last.x, y: prev.y + e.clientY - last.y }))
        }
    }

    function handlePointerUp(e) {
        pointers.current.delete(e.pointerId)
        if (pointers.current.size < 2) pinch.current = null
    }

    const gestures = {
        onZoom: handleWheel,
        enabled: active,
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
    }

    const frameSx = { position: 'relative', aspectRatio: '1', overflow: 'hidden', borderRadius: 1, ...(active ? { touchAction: 'none', cursor: 'grab', '&:active': { cursor: 'grabbing' } } : { cursor: 'pointer' }) }
    const imgStyle = { width: '100%', height: '100%', objectFit: 'contain', transform, transformOrigin: '0 0', display: 'block', userSelect: 'none' }

    const original = <img src={(full && rawFullSrc) || rawSrc} alt={t('photoReview.compare.original', { label })} style={imgStyle} draggable={false} />
    // While a new version is generated the old edit is not shown at all, not
    // even greyed out: it is about to be replaced, and must not be mistaken
    // for the new one.
    const edit = !busy && <img src={(full && editedFullSrc) || editedSrc} alt={t('photoReview.compare.edit', { label })} style={imgStyle} draggable={false} />
    const spinner = busy && (
        <Box role='status' sx={{ position: 'absolute', inset: 0, zIndex: 1, bgcolor: 'background.paper', color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1 }}>
            <CircularProgress size={28} />
            <Typography variant='body2'>{t('photoReview.compare.reediting')}</Typography>
        </Box>
    )

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                <ToggleButtonGroup size='small' exclusive value={mode} onChange={(_, value) => value && setMode(value)}>
                    <ToggleButton value='side'>{t('photoReview.compare.sideBySide')}</ToggleButton>
                    <ToggleButton value='wipe'>{t('photoReview.compare.wipe')}</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup size='small' exclusive value={backdrop} onChange={(_, value) => value && setBackdrop(value)}>
                    {Object.keys(BACKDROPS).map(key => (
                        <ToggleButton key={key} value={key}>{t(`photoReview.compare.${key}`)}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Button size='small' onClick={() => setView(IDENTITY)}>{t('photoReview.compare.resetZoom')}</Button>
            </Box>

            {mode === 'side' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    <ZoomFrame sx={frameSx} {...gestures}>{original}</ZoomFrame>
                    <ZoomFrame sx={{ ...frameSx, ...BACKDROPS[backdrop] }} data-backdrop={backdrop} {...gestures}>{edit}{spinner}</ZoomFrame>
                </Box>
            ) : (
                <Box>
                    <ZoomFrame sx={{ ...frameSx, maxWidth: 640, mx: 'auto', ...BACKDROPS[backdrop] }} data-backdrop={backdrop} {...gestures}>
                        {edit}
                        <Box sx={{ position: 'absolute', inset: 0 }} style={{ clipPath: `inset(0 ${100 - wipe}% 0 0)` }}>{original}</Box>
                        {spinner}
                    </ZoomFrame>
                    <Slider
                        value={wipe}
                        onChange={(_, value) => setWipe(value)}
                        min={0}
                        max={100}
                        sx={{ maxWidth: 640, mx: 'auto', display: 'block' }}
                        getAriaLabel={() => t('photoReview.compare.wipePosition')}
                    />
                </Box>
            )}
        </Box>
    )
}
